// Session persistence for rejoining a game after a refresh or a closed tab.
//
// Stored in localStorage so it survives closing the tab. It's shared by every
// tab, so opening the site in a new tab rejoins the same game there — the
// server hands the player slot to whoever rejoins last. That's deliberate:
// never losing your place in the game matters more than multi-tab play.

const SESSION_KEY = 'pictaphone_session';
const MAX_AGE_MS = 12 * 60 * 60 * 1000; // past this the room has long expired

export const getSavedSession = () => {
  try { return JSON.parse(localStorage.getItem(SESSION_KEY)); } catch { return null; }
};

export const saveSession = (roomCode, playerName) => {
  try {
    localStorage.setItem(SESSION_KEY, JSON.stringify({ roomCode, playerName, savedAt: Date.now() }));
  } catch { /* storage unavailable — session just won't persist */ }
};

export const clearSession = () => {
  clearDrawingDraft();
  try { localStorage.removeItem(SESSION_KEY); } catch { /* ignore */ }
};

// The saved session to rejoin on page load, or null if there's none or it's
// too old to still be a live game.
export const getSessionToResume = () => {
  const saved = getSavedSession();
  if (!saved?.roomCode || !saved?.playerName) return null;
  if (!saved.savedAt || Date.now() - saved.savedAt > MAX_AGE_MS) {
    clearSession();
    return null;
  }
  return saved;
};

// ─── In-progress drawing draft ───
// Keyed by room + player name so a draft can't carry over into another game.
const DRAFT_PREFIX = 'pictaphone_drawing_draft:';

const draftKey = () => {
  const s = getSavedSession();
  return s ? `${DRAFT_PREFIX}${s.roomCode}:${s.playerName}` : null;
};

export const saveDrawingDraft = (assignmentText, strokes) => {
  const key = draftKey();
  if (!key) return;
  try {
    localStorage.setItem(key, JSON.stringify({ assignmentText, strokes, savedAt: Date.now() }));
  } catch { /* quota exceeded or unavailable — draft just won't persist */ }
};

// Returns the saved strokes if they belong to this assignment, else null.
export const loadDrawingDraft = (assignmentText) => {
  const key = draftKey();
  if (!key) return null;
  try {
    const saved = JSON.parse(localStorage.getItem(key));
    return saved?.assignmentText === assignmentText && saved.strokes?.length > 0
      ? saved.strokes
      : null;
  } catch { return null; }
};

export const clearDrawingDraft = () => {
  const key = draftKey();
  if (!key) return;
  try { localStorage.removeItem(key); } catch { /* ignore */ }
};

// Drop drafts abandoned by tabs that never came back (games long over).
try {
  for (let i = localStorage.length - 1; i >= 0; i--) {
    const key = localStorage.key(i);
    if (!key?.startsWith(DRAFT_PREFIX)) continue;
    let savedAt = 0;
    try { savedAt = JSON.parse(localStorage.getItem(key))?.savedAt || 0; } catch { /* corrupt */ }
    if (Date.now() - savedAt > MAX_AGE_MS) localStorage.removeItem(key);
  }
} catch { /* storage unavailable */ }
